/**
 * frontend/src/pages/Leaderboard.jsx
 * SoundDAO Sprint 2 M3 — Global leaderboard + creator feed
 *
 * Route: /leaderboard  (public — no auth required)
 *
 * Tabs (in order):
 *   1. Feed      — aggregated creator posts, newest first, paginated (DEFAULT)
 *   2. Players   — player leaderboard table
 *   3. Creators  — creator leaderboard table
 *
 * Props:
 *   session — Supabase auth session (may be null)
 *   user    — users row ({ id, persona, city, ... }) — may be null
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ScoutBadge from '../components/ScoutBadge';
import DayOneBadge from '../components/DayOneBadge';
import ArchitectBadge from '../components/ArchitectBadge';
import './Leaderboard.css';
import { authFetch } from '../lib/authFetch';
import { supabase } from '../lib/supabaseClient';
import { PUBLIC_APP_URL } from '../lib/constants';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const FREN_STATUS_LABEL = {
  not_started: 'Coming Soon',
  in_progress: 'In Progress',
  launched:    '🚀 Live',
};

const POST_TYPE_ICON = {
  text:  '✏️',
  photo: '📷',
  link:  '🔗',
  video: '🎬',
};

// ── Link post resolution ───────────────────────────────────────────────────────
// Feed posts with post_type === 'link' store the target URL in post.content.
// We extract a clean URL and derive a human-readable platform label from its
// host, so the card can render "Listen on SoundCloud →" instead of the raw
// URL.  Falls back to post.platform (the upload-time platform field) if we
// can't recognize the host, or to the generic label "Link".
const LINK_PLATFORMS = [
  { match: /(^|\.)soundcloud\.|\bon\.soundcloud\./, label: 'SoundCloud' },
  { match: /(^|\.)spotify\./,                        label: 'Spotify'    },
  { match: /(youtube\.com|youtu\.be)/,               label: 'YouTube'    },
  { match: /music\.apple\./,                         label: 'Apple Music' },
  { match: /bandcamp\./,                             label: 'Bandcamp'   },
];

function resolveLinkPost(post) {
  const raw = String(post?.content || '').trim();
  if (!raw) return { url: null, label: null };

  // Pull the first http(s) URL out of the content — robust to leading/
  // trailing whitespace or an accidentally-saved platform prefix.
  const match = raw.match(/https?:\/\/[^\s]+/i);
  const url   = match ? match[0] : null;
  if (!url) return { url: null, label: post?.platform || 'Link' };

  let host = '';
  try { host = new URL(url).hostname.toLowerCase(); } catch { /* host stays '' */ }

  const entry = LINK_PLATFORMS.find(p => p.match.test(host));
  const label = entry?.label || post?.platform || 'Link';
  return { url, label };
}

// ── Time-ago helper ───────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const ms = Date.now() - new Date(dateStr).getTime();
  if (ms < 60_000)       return 'just now';
  if (ms < 3_600_000)    return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000)   return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

// ── Leaderboard ───────────────────────────────────────────────────────────────
export default function Leaderboard({ session, user }) {
  const navigate  = useNavigate();
  const user_id   = session?.user?.id || null;
  const isPlayer  = user?.persona === 'player';

  // ── Tab state — Feed is default ───────────────────────────────────────────
  const [activeTab,    setActiveTab]    = useState('feed');

  // ── Leaderboard state (players / creators) ────────────────────────────────
  const [cities,       setCities]       = useState([]);
  const [cityFilter,   setCityFilter]   = useState('');
  const [board,        setBoard]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [ageMinutes,   setAgeMinutes]   = useState(null);
  const [myRank,       setMyRank]       = useState(null);
  const [rankLoading,  setRankLoading]  = useState(false);
  const [shareDone,    setShareDone]    = useState(false);


  // Sprint 6-Zero M6 item 7: at 11 users every streak is 0, so the column
  // rendered as a wall of dashes and made the whole board look abandoned.
  // It appears the moment ANY player reaches a 3-day streak.
  const showStreakColumn = React.useMemo(
    () => board.some(r => (Number(r.streak_count) || 0) >= 3),
    [board],
  );

  // ── Feed state ────────────────────────────────────────────────────────────
  const [feedPosts,    setFeedPosts]    = useState([]);
  const [feedLoading,  setFeedLoading]  = useState(true);
  const [feedPage,     setFeedPage]     = useState(1);
  const [feedHasMore,  setFeedHasMore]  = useState(false);
  const [feedLoadingMore, setFeedLoadingMore] = useState(false);

  // ── First-time feed welcome banner ────────────────────────────────────────
  // Shows once on the first return visit that lands here, then never again.
  const [feedWelcomeDismissed, setFeedWelcomeDismissed] = useState(
    () => localStorage.getItem('sounddao_feed_welcomed') === 'true'
  );
  const showFeedWelcome = !feedWelcomeDismissed &&
    localStorage.getItem('sounddao_returning') === 'true';
  const dismissFeedWelcome = () => {
    localStorage.setItem('sounddao_feed_welcomed', 'true');
    setFeedWelcomeDismissed(true);
  };

  // ── Fetch cities for dropdown ─────────────────────────────────────────────
  useEffect(() => {
    authFetch(`${API_BASE}/api/leaderboard/cities`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCities(d.cities || []); })
      .catch(() => {});
  }, []);

  // ── Fetch leaderboard data ─────────────────────────────────────────────────
  const fetchBoard = useCallback(async () => {
    if (activeTab === 'feed') return;
    setLoading(true);
    try {
      const cityParam = cityFilter ? `?city=${encodeURIComponent(cityFilter)}` : '';
      const url = `${API_BASE}/api/leaderboard/${activeTab}${cityParam}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setBoard(data.leaderboard || []);
      setAgeMinutes(data.age_minutes ?? 0);
    } catch (err) {
      console.error('[Leaderboard] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, cityFilter]);

  useEffect(() => {
    if (activeTab !== 'feed') fetchBoard();
  }, [fetchBoard, activeTab]);

  // Sprint 6A.1: player rows no longer carry user ids (privacy). Streaks come in the row
  // (streak_count); scout badges were read from a scout_badges table that does not exist, so
  // that lookup is gone. The caller's own row is flagged by the server (is_me).

  // ── Fetch personal rank (Players only) ────────────────────────────────────
  useEffect(() => {
    if (!user_id || !isPlayer) return;
    setRankLoading(true);
    authFetch(`${API_BASE}/api/leaderboard/players/me?user_id=${user_id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setMyRank(d); })
      .catch(() => {})
      .finally(() => setRankLoading(false));
  }, [user_id, isPlayer]);

  // ── Fetch feed (page 1 on mount / tab switch) ─────────────────────────────
  const fetchFeed = useCallback(async (page = 1, append = false) => {
    if (append) setFeedLoadingMore(true);
    else        setFeedLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/posts/feed?page=${page}&limit=20`);
      if (!res.ok) throw new Error('feed fetch failed');
      const data = await res.json();
      if (append) {
        setFeedPosts(prev => [...prev, ...(data.posts || [])]);
      } else {
        setFeedPosts(data.posts || []);
      }
      setFeedHasMore(data.has_more || false);
      setFeedPage(page);
    } catch (err) {
      console.error('[Leaderboard] feed fetch error:', err);
    } finally {
      setFeedLoading(false);
      setFeedLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'feed') fetchFeed(1, false);
  }, [activeTab, fetchFeed]);

  // ── Share rank on X ────────────────────────────────────────────────────────
  const handleShareRank = () => {
    if (!myRank?.global_rank) return;
    const text = encodeURIComponent(
      `I'm ranked #${myRank.global_rank} in Big Creative on @SoundDAO with ${(myRank.total_earned || 0).toLocaleString()} SOUND points. Join me: ${PUBLIC_APP_URL}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'noopener');
    setShareDone(true);
    setTimeout(() => setShareDone(false), 3000);
  };

  // ── Determine if a leaderboard row is the current user ────────────────────
  const isMyRow = (row) => Boolean(row.is_me);

  // ── Creators this player already recruited (Sprint 6A.1) ─────────────────
  // From the player's own recruit_creator rewards (metadata.target_id; RLS lets a user read
  // only their own rewards). Sprint 6A.1.
  const [recruitedIds, setRecruitedIds] = useState(() => new Set());
  useEffect(() => {
    if (!user_id || !isPlayer) { setRecruitedIds(new Set()); return; }
    let alive = true;
    supabase
      .from('rewards')
      .select('metadata')
      .eq('user_id', user_id)
      .eq('action_type', 'recruit_creator')
      .then(({ data }) => {
        if (alive) setRecruitedIds(new Set((data || []).map((r) => r.metadata?.target_id).filter(Boolean)));
      }, () => {});
    return () => { alive = false; };
  }, [user_id, isPlayer]);

  // ── Recruit button handler — navigates to creator profile ─────────────────
  const handleRecruit = (creatorUserId) => {
    navigate(`/creator/${creatorUserId}`);
  };

  return (
    <div className="lb-screen">
      {/* Nav */}
      <nav className="lb-nav">
        <Link to="/creators" className="lb-nav-back">← Discover Artists</Link>
        {session && (
          <Link
            to={`/dashboard/${user?.persona || 'player'}`}
            className="lb-nav-dashboard"
          >
            Dashboard
          </Link>
        )}
      </nav>

      <div className="lb-content">
        {/* Page title */}
        <div className="lb-header">
          <h1 className="lb-title">🏆 Big Creative</h1>
          <p className="lb-subtitle">Global Rankings</p>
        </div>

        {/* ── First-return welcome banner ── */}
        {showFeedWelcome && (
          <div className="lb-welcome-banner">
            <span className="lb-welcome-text">
              🏠 Welcome to the community feed — your new home on SoundDAO.
            </span>
            <button className="lb-welcome-dismiss" onClick={dismissFeedWelcome} aria-label="Dismiss">
              ✕
            </button>
          </div>
        )}

        {/* ── Personal rank card (Task 3.4) — Players only ── */}
        {isPlayer && (
          <div className="lb-my-rank-card">
            {rankLoading ? (
              <div className="lb-my-rank-loading">Loading your rank…</div>
            ) : myRank?.global_rank ? (
              <>
                <div className="lb-my-rank-stats">
                  <div className="lb-my-rank-stat">
                    <div className="lb-my-rank-number">#{myRank.global_rank}</div>
                    <div className="lb-my-rank-label">Global Rank</div>
                  </div>
                  {myRank.city_rank && myRank.city && (
                    <>
                      <div className="lb-my-rank-divider" />
                      <div className="lb-my-rank-stat">
                        <div className="lb-my-rank-number">#{myRank.city_rank}</div>
                        <div className="lb-my-rank-label">In {myRank.city}</div>
                      </div>
                    </>
                  )}
                  <div className="lb-my-rank-divider" />
                  <div className="lb-my-rank-stat">
                    <div className="lb-my-rank-number">{(myRank.total_earned || 0).toLocaleString()}</div>
                    <div className="lb-my-rank-label">SOUND Earned</div>
                  </div>
                </div>
                {/* Task 3.5 — Share rank button */}
                <button
                  className={`lb-share-btn ${shareDone ? 'lb-share-btn--done' : ''}`}
                  onClick={handleShareRank}
                >
                  {shareDone ? '✓ Tweet sent!' : '𝕏 Share my rank'}
                </button>
              </>
            ) : (
              <p className="lb-my-rank-empty">
                Earn SOUND to appear on the leaderboard.
              </p>
            )}
          </div>
        )}

        {/* ── Tab bar — Feed · Players · Creators ── */}
        <div className="lb-controls">
          <div className="lb-tabs">
            <button
              className={`lb-tab ${activeTab === 'feed' ? 'lb-tab--active' : ''}`}
              onClick={() => setActiveTab('feed')}
            >
              📰 Feed
            </button>
            <button
              className={`lb-tab ${activeTab === 'players' ? 'lb-tab--active' : ''}`}
              onClick={() => setActiveTab('players')}
            >
              🎧 Players
            </button>
            <button
              className={`lb-tab ${activeTab === 'creators' ? 'lb-tab--active' : ''}`}
              onClick={() => setActiveTab('creators')}
            >
              🎤 Creators
            </button>
          </div>

          {/* City filter + refresh — only for leaderboard tabs */}
          {activeTab !== 'feed' && (
            <>
              <select
                className="lb-city-filter"
                value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
              >
                <option value="">All Cities</option>
                {cities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <div className="lb-refresh-row">
                <button className="lb-refresh-btn" onClick={fetchBoard} disabled={loading}>
                  {loading ? '⟳' : '↻ Refresh'}
                </button>
                {ageMinutes !== null && (
                  <span className="lb-age">
                    {ageMinutes === 0 ? 'Just updated' : `Updated ${ageMinutes}m ago`}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            FEED TAB
            ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'feed' && (
          <div className="lb-feed">
            {feedLoading ? (
              <div className="lb-loading"><div className="lb-spinner" /></div>
            ) : feedPosts.length === 0 ? (
              <div className="lb-feed-empty">
                <p className="lb-feed-empty-title">No updates yet</p>
                <p className="lb-feed-empty-sub">Creators will post here soon — check back shortly.</p>
              </div>
            ) : (
              <>
                {feedPosts.map(post => {
                  const alreadyRecruited = recruitedIds.has(post.creator_id);
                  const showRecruit      = isPlayer && !alreadyRecruited && user_id;
                  return (
                    <div key={post.id} className="lb-feed-card">
                      {/* Card header: avatar + name + time */}
                      <div className="lb-feed-card-header">
                        <div className="lb-feed-avatar">
                          {post.avatar_url ? (
                            <img
                              src={post.avatar_url}
                              alt={post.artist_name}
                              className="lb-feed-avatar-img"
                            />
                          ) : (
                            <div className="lb-feed-avatar-placeholder">🎤</div>
                          )}
                        </div>
                        <div className="lb-feed-card-meta">
                          <Link
                            to={`/creator/${post.creator_id}`}
                            className="lb-feed-artist-name"
                          >
                            {post.artist_name}
                          </Link>
                          <div className="lb-feed-card-detail">
                            <span className="lb-feed-type-icon">{POST_TYPE_ICON[post.post_type] || '📝'}</span>
                            {post.platform && (
                              <span className="lb-feed-platform-chip">{post.platform}</span>
                            )}
                            <span className="lb-feed-time">{timeAgo(post.created_at)}</span>
                          </div>
                        </div>
                        {showRecruit && (
                          <button
                            className="lb-feed-recruit-btn"
                            onClick={() => handleRecruit(post.creator_id)}
                            title="View this creator's profile"
                          >
                            ➕ Recruit
                          </button>
                        )}
                      </div>

                      {/* Card body: content */}
                      <div className="lb-feed-card-body">
                        {/* Text / caption */}
                        {post.content && post.post_type !== 'link' && (
                          <p className="lb-feed-text">{post.content}</p>
                        )}

                        {/* Link post — render as a clean tappable pill; never
                            expose the raw URL.  Platform detected from the
                            URL host, falling back to post.platform then a
                            generic label. */}
                        {post.post_type === 'link' && post.content && (() => {
                          const { url, label } = resolveLinkPost(post);
                          if (!url) return null;
                          return (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="lb-feed-link"
                            >
                              🎵 Listen on {label} →
                            </a>
                          );
                        })()}

                        {/* Photo */}
                        {post.post_type === 'photo' && post.media_url && (
                          <img
                            src={post.media_url}
                            alt="Post"
                            className="lb-feed-media-img"
                          />
                        )}

                        {/* Video */}
                        {post.post_type === 'video' && post.media_url && (
                          <video
                            src={post.media_url}
                            className="lb-feed-media-video"
                            controls
                            playsInline
                            preload="metadata"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Load more */}
                {feedHasMore && (
                  <button
                    className="lb-feed-load-more"
                    onClick={() => fetchFeed(feedPage + 1, true)}
                    disabled={feedLoadingMore}
                  >
                    {feedLoadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PLAYERS TAB
            ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'players' && (
          loading ? (
            <div className="lb-loading"><div className="lb-spinner" /></div>
          ) : board.length === 0 ? (
            <div className="lb-empty">
              {cityFilter
                ? `No players found in ${cityFilter} yet.`
                : 'No players on the leaderboard yet.'}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="lb-table-wrap lb-desktop-only">
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th className="lb-th lb-th--rank">#</th>
                      <th className="lb-th lb-th--name">Player</th>
                      {showStreakColumn && <th className="lb-th lb-th--streak">Streak</th>}
                      <th className="lb-th lb-th--title">Title</th>
                      <th className="lb-th lb-th--earned">SOUND Earned</th>
                      <th className="lb-th lb-th--city">City</th>
                      {user_id && <th className="lb-th lb-th--actions" />}
                    </tr>
                  </thead>
                  <tbody>
                    {board.map(row => {
                      const mine = isMyRow(row);
                      return (
                        <tr
                          key={row.user_id || row.rank}
                          className={`lb-row ${mine ? 'lb-row--me' : ''}`}
                        >
                          <td className="lb-td lb-td--rank">
                            {row.rank <= 3 ? (
                              <span className="lb-medal">
                                {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : '🥉'}
                              </span>
                            ) : row.rank}
                          </td>
                          <td className="lb-td lb-td--name">
                            <span className={`lb-name ${mine ? 'lb-name--me' : ''}`}>
                              {row.display_name}
                              {mine && <span className="lb-you-badge">you</span>}
                              <ScoutBadge badges={row.badges || []} />
                              <DayOneBadge dayOne={row.day_one} />
                              <ArchitectBadge count={row.architect_count} />
                            </span>
                          </td>
{showStreakColumn && (
                                                      <td className="lb-td lb-td--streak">
                            {(row.streak_count || 0) > 0
                              ? `🔥 ${row.streak_count}d`
                              : '—'}
                          </td>
                          )}
                          <td className="lb-td lb-td--title">
                            <span className="lb-rank-pill">{row.rank_title}</span>
                          </td>
                          <td className="lb-td lb-td--earned">
                            {(row.total_earned ?? 0).toLocaleString()}
                          </td>
                          <td className="lb-td lb-td--city">{row.city || '—'}</td>
                          {user_id && (
                            <td className="lb-td lb-td--actions">
                              {mine && (
                                <button
                                  className={`lb-share-row-btn ${shareDone ? 'lb-share-row-btn--done' : ''}`}
                                  onClick={handleShareRank}
                                  title="Share my rank"
                                >
                                  {shareDone ? '✓' : '𝕏'}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="lb-mobile-only lb-cards">
                {board.map(row => {
                  const mine = isMyRow(row);
                  return (
                    <div
                      key={row.user_id || row.rank}
                      className={`lb-card ${mine ? 'lb-card--me' : ''}`}
                    >
                      <div className="lb-card-rank">
                        {row.rank <= 3
                          ? <span className="lb-medal">{row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : '🥉'}</span>
                          : <span className="lb-card-rank-num">#{row.rank}</span>
                        }
                      </div>
                      <div className="lb-card-body">
                        <div className="lb-card-name">
                          {row.display_name}
                          {mine && <span className="lb-you-badge">you</span>}
                          <ScoutBadge badges={row.badges || []} />
                          <DayOneBadge dayOne={row.day_one} />
                              <ArchitectBadge count={row.architect_count} />
                        </div>
                        {(row.streak_count || 0) > 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#f97316', marginTop: '2px' }}>
                            🔥 {row.streak_count}d streak
                          </div>
                        )}
                        <div className="lb-card-meta">
                          <span className="lb-rank-pill">{row.rank_title}</span>
                          {row.city && <span className="lb-card-city">{row.city}</span>}
                        </div>
                      </div>
                      <div className="lb-card-right">
                        <div className="lb-card-earned">{(row.total_earned ?? 0).toLocaleString()}</div>
                        <div className="lb-card-earned-label">SOUND</div>
                        {mine && user_id && (
                          <button
                            className={`lb-share-row-btn ${shareDone ? 'lb-share-row-btn--done' : ''}`}
                            onClick={handleShareRank}
                            title="Share my rank"
                          >
                            {shareDone ? '✓' : '𝕏'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )
        )}

        {/* ══════════════════════════════════════════════════════════════════
            CREATORS TAB
            ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'creators' && (
          loading ? (
            <div className="lb-loading"><div className="lb-spinner" /></div>
          ) : board.length === 0 ? (
            <div className="lb-empty">
              {cityFilter
                ? `No creators found in ${cityFilter} yet.`
                : 'No creators on the leaderboard yet.'}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="lb-table-wrap lb-desktop-only">
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th className="lb-th lb-th--rank">#</th>
                      <th className="lb-th lb-th--name">Artist</th>
                      <th className="lb-th lb-th--genre">Genre</th>
                      <th className="lb-th lb-th--fans">Fans</th>
                      <th className="lb-th lb-th--heat">Heat</th>
                      <th className="lb-th lb-th--stage">Stage</th>
                      <th className="lb-th lb-th--fren">FReN</th>
                      <th className="lb-th lb-th--city">City</th>
                    </tr>
                  </thead>
                  <tbody>
                    {board.map(row => {
                      const mine = isMyRow(row);
                      return (
                        <tr
                          key={row.user_id || row.rank}
                          className={`lb-row ${mine ? 'lb-row--me' : ''}`}
                        >
                          <td className="lb-td lb-td--rank">
                            {row.rank <= 3 ? (
                              <span className="lb-medal">
                                {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : '🥉'}
                              </span>
                            ) : row.rank}
                          </td>
                          <td className="lb-td lb-td--name">
                            <Link
                              to={`/creator/${row.user_id}`}
                              className={`lb-creator-link ${mine ? 'lb-name--me' : ''}`}
                            >
                              {row.artist_name}
                              {mine && <span className="lb-you-badge">you</span>}
                            </Link>
                          </td>
                          <td className="lb-td lb-td--genre">
                            {row.genre ? <span className="lb-genre-chip">{row.genre}</span> : '—'}
                          </td>
                          <td className="lb-td lb-td--fans">{(row.total_fans ?? 0).toLocaleString()}</td>
                          <td className="lb-td lb-td--heat">
                            <span className={`lb-heat-pill lb-heat-pill--${(row.heat_tier || 'Cold').toLowerCase()}`}>
                              {row.heat_tier || 'Cold'}
                            </span>
                          </td>
                          <td className="lb-td lb-td--stage">
                            {row.stage && row.stage !== 'Raw'
                              ? <span className="lb-stage-pill">{row.stage}</span>
                              : '—'}
                          </td>
                          <td className="lb-td lb-td--fren">
                            <span className={`lb-fren-badge lb-fren-badge--${row.fren_status}`}>
                              {FREN_STATUS_LABEL[row.fren_status] || row.fren_status}
                            </span>
                          </td>
                          <td className="lb-td lb-td--city">{row.city || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="lb-mobile-only lb-cards">
                {board.map(row => {
                  const mine = isMyRow(row);
                  return (
                    <Link
                      key={row.user_id || row.rank}
                      to={`/creator/${row.user_id}`}
                      className={`lb-card lb-card--creator ${mine ? 'lb-card--me' : ''}`}
                    >
                      <div className="lb-card-rank">
                        {row.rank <= 3
                          ? <span className="lb-medal">{row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : '🥉'}</span>
                          : <span className="lb-card-rank-num">#{row.rank}</span>
                        }
                      </div>
                      <div className="lb-card-body">
                        <div className="lb-card-name">
                          {row.artist_name || 'Unknown Artist'}
                          {mine && <span className="lb-you-badge">you</span>}
                        </div>
                        <div className="lb-card-meta">
                          <span className={`lb-heat-pill lb-heat-pill--${(row.heat_tier || 'Cold').toLowerCase()}`}>
                            {row.heat_tier || 'Cold'}
                          </span>
                          {row.stage && row.stage !== 'Raw' && (
                            <span className="lb-stage-pill">{row.stage}</span>
                          )}
                          {row.city && <span className="lb-card-city">{row.city}</span>}
                        </div>
                      </div>
                      <div className="lb-card-right">
                        <div className="lb-card-earned">{(row.total_fans ?? 0).toLocaleString()}</div>
                        <div className="lb-card-earned-label">Fans</div>
                        <span className={`lb-fren-badge lb-fren-badge--${row.fren_status}`}>
                          {row.fren_status === 'launched' ? '🚀 Live' : 'FReN'}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}
